import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Card, Row, Col, Tag, Button, message, Carousel } from 'antd';
import { ThemeContext } from '../../context/ThemeContext';
import {
  SafetyOutlined,
  DollarOutlined,
  LineChartOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import './BondTypesSectionRedesign.css';
import apiClient from '../../services/apiClient';

const { Title, Paragraph } = Typography;

const BondTypesSectionRedesign = () => {
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);
  const [loading, setLoading] = useState(true);
  const [bondTypesData, setBondTypesData] = useState([]);

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  // Simplified bond type configurations
  const bondTypes = [
    {
      id: 1,
      type: 'High Quality Liquid Bonds',
      apiCategory: 'highqualityliquidbonds',
      description: 'Corporate bonds with high credit ratings and excellent liquidity',
      icon: <DollarOutlined />,
      color: '#1890ff',
      features: [
        'AAA/AA credit rating',
        'High liquidity',
        'Regular coupon payments',
        'Market-traded'
      ]
    },
    {
      id: 2,
      type: 'Regular Income Bonds',
      apiCategory: 'regularincomebonds',
      description: 'Bonds designed for regular income generation with moderate risk',
      icon: <LineChartOutlined />,
      color: '#722ed1',
      features: [
        'Regular income stream',
        'Moderate risk profile',
        'Diversified portfolio',
        'Quarterly payments'
      ]
    },
    {
      id: 3,
      type: 'State Government Securities',
      apiCategory: 'stategovernment',
      description: 'Government-backed securities with the highest credit rating and lowest risk',
      icon: <SafetyOutlined />,
      color: '#52c41a',
      features: [
        'Government guarantee',
        'Highest credit rating',
        'Lowest default risk',
        'Stable returns'
      ]
    }
  ];

  // Fetch data for each bond type (simplified)
  const fetchBondTypeData = async (bondType) => {
    try {
      const response = await apiClient.get(`${baseUrl}/isin-category/category/${bondType.apiCategory}?page=0&size=1`);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;

      if (result.statusCode === 200 && result.data && result.data.content) {
        return {
          ...bondType,
          totalIsins: result.data.totalElements || 0
        };
      } else {
        console.error('Invalid API response structure:', result);
        return bondType;
      }
    } catch (error) {
      console.error(`Error fetching data for ${bondType.type}:`, error);
      return bondType;
    }
  };

  // Fetch all bond type data
  useEffect(() => {
    const fetchAllBondTypeData = async () => {
      setLoading(true);
      try {
        const bondTypesWithData = await Promise.all(
          bondTypes.map(bondType => fetchBondTypeData(bondType))
        );
        setBondTypesData(bondTypesWithData);
      } catch (error) {
        console.error('Error fetching bond type data:', error);
        setBondTypesData(bondTypes);
      } finally {
        setLoading(false);
      }
    };

    fetchAllBondTypeData();
  }, []);

  const handleBondTypeClick = (bondType) => {
    // Navigate to bond type specific page or filter
    console.log('Clicked on bond type:', bondType.type);
    // You can add navigation logic here
  };

    const handleExploreClick = (e) => {
    e.stopPropagation(); // Prevent card click event
    navigate('/isins');
  };

  return (
    <motion.section 
      className={`bond-types-section ${isDark ? "dark" : ""}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
    >
      <div className="section-header">
        <Title level={2} className="section-title">
          Explore Instruments by Categories
        </Title>
        <Paragraph className="section-subtitle">
          Discover different categories of bonds tailored to your investment goals
        </Paragraph>
      </div>

      <div className="container">
        {/* Desktop View */}
        <div className="desktop-bond-types">
          <Row gutter={[24, 24]}>
            {(loading ? bondTypes : bondTypesData).map((bondType, i) => (
              <Col xs={24} lg={8} key={bondType.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.2 }}
                >
                  <Card
                  className="bond-type-card"
                  hoverable
                  onClick={() => handleBondTypeClick(bondType)}
                >
                  <div className="bond-type-header">
                    <div className="bond-type-icon" style={{ color: bondType.color }}>
                      {bondType.icon}
                    </div>
                    <div className="bond-type-info">
                      <Title level={4} className="bond-type-title">
                        {bondType.type}
                      </Title>
                      <Paragraph className="bond-type-description">
                        {bondType.description}
                      </Paragraph>
                    </div>
                  </div>

                  <div className="bond-type-features">
                    {bondType.features.map((feature, index) => (
                      <Tag key={index} color={bondType.color} className="feature-tag">
                        {feature}
                      </Tag>
                    ))}
                  </div>

                  <div className="bond-type-footer">
                    <div className="bond-type-count">
                      {loading ? (
                        <span className="loading-text">Loading...</span>
                      ) : (
                        <span>{bondType.totalIsins || 0} ISINs available</span>
                      )}
                    </div>
                    <Button 
                      type="primary" 
                      className="explore-button"
                      icon={<ArrowRightOutlined />}
                      onClick={handleExploreClick}
                    >
                      Explore
                    </Button>
                  </div>
                </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Mobile Carousel View */}
        <div className="mobile-bond-types">
          <Carousel
            dots={{ position: 'bottom' }}
            autoplay={true}
            autoplaySpeed={3000}
            infinite={true}
            slidesToShow={1}
            slidesToScroll={1}
            responsive={[
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                  dots: true,
                  arrows: true,
                  adaptiveHeight: true,
                  autoplay: true,
                  autoplaySpeed: 3000
                }
              }
            ]}
            adaptiveHeight={true}
            className="bond-types-carousel"
          >
            {(loading ? bondTypes : bondTypesData).map((bondType) => (
              <div key={bondType.id} className="carousel-slide">
                <Card
                  className="bond-type-card"
                  hoverable
                  onClick={() => handleBondTypeClick(bondType)}
                >
                  <div className="bond-type-header">
                    <div className="bond-type-icon" style={{ color: bondType.color }}>
                      {bondType.icon}
                    </div>
                    <div className="bond-type-info">
                      <Title level={4} className="bond-type-title">
                        {bondType.type}
                      </Title>
                      <Paragraph className="bond-type-description">
                        {bondType.description}
                      </Paragraph>
                    </div>
                  </div>

                  <div className="bond-type-features">
                    {bondType.features.map((feature, index) => (
                      <Tag key={index} color={bondType.color} className="feature-tag">
                        {feature}
                      </Tag>
                    ))}
                  </div>

                  <div className="bond-type-footer">
                    <div className="bond-type-count">
                      {loading ? (
                        <span className="loading-text">Loading...</span>
                      ) : (
                        <span>{bondType.totalIsins || 0} ISINs available</span>
                      )}
                    </div>
                    <Button 
                      type="primary" 
                      className="explore-button"
                      icon={<ArrowRightOutlined />}
                      onClick={handleExploreClick}
                    >
                      Explore
                    </Button>
                  </div>
                </Card>
              </div>
            ))}
          </Carousel>
        </div>
      </div>
    </motion.section>
  );
};

export default BondTypesSectionRedesign;
